import { useMemo } from "react";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type FlightFaresSelection,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";

import { Icon, InfoBanner, InfoBannerSeverity } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightCardFareSelector.module.scss";

export interface FlightCardFareSelectorProps {
  sliceIndex: number;
  faresSelection: FlightFaresSelection;
  className?: string;
}

export const FlightCardFareSelector = (props: FlightCardFareSelectorProps) => {
  const [block, cn] = useModuleBEM(
    useFlightStyles(CoreFlightsComponent.FlightCardFareSelector, defaultStyles),
    CoreFlightsComponent.FlightCardFareSelector
  );

  const { t } = useI18nContext();
  const {
    faresSelection: { fares, selectedFare, setSelectedFare },
    sliceIndex,
    className,
  } = props;

  const selectedSlice = selectedFare?.fareDetails?.slices[sliceIndex];
  const isVi =
    selectedSlice?.fareDetails.segments.some(
      (segment) => segment.isSelfTransferLayover ?? false
    ) ?? false;

  // for style backward compatibility
  const flightCardFareListItemBem = useModuleBEM(
    useFlightStyles(CoreFlightsComponent.FlightCardFareListItem, defaultStyles),
    CoreFlightsComponent.FlightCardFareListItem
  );

  const fareItems = useMemo(
    () =>
      fares.map((fare) => {
        const isSelectedFare = selectedFare?.key === fare.key;

        return (
          <li
            key={`fare-${fare.key}-${fare.origin}`}
            className={cn("fare-list-item-container", {
              selected: isSelectedFare,
            })}
          >
            <div
              className={clsx(
                flightCardFareListItemBem[0],
                cn("fare-list-item")
              )}
            >
              <fare.View
                fareKey={fare.key}
                fareDetails={fare.fareDetails}
                tripDetails={fare.tripDetails}
                sliceIndex={sliceIndex}
                isSelected={isSelectedFare}
                onSelect={() => setSelectedFare(fare)}
              />
            </div>
          </li>
        );
      }),
    [
      fares,
      selectedFare?.key,
      cn,
      flightCardFareListItemBem,
      sliceIndex,
      setSelectedFare,
    ]
  );

  return (
    <div className={clsx(block, className)}>
      {isVi ? (
        <div className={cn("vi-banner")}>
          <InfoBanner severity={InfoBannerSeverity.INFO}>
            <Icon iconName={IconNameEnum.airplaneDiagonal} />{" "}
            {t("core-flights.vi.multipleAirlinesNotice")}
          </InfoBanner>
        </div>
      ) : null}
      <ul className={cn("fare-list")}>{fareItems}</ul>
    </div>
  );
};
